import { GlobeAltIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { LightBulbIcon } from '@heroicons/react/24/solid';
import { ReactNode } from 'react';

interface Headline {
  icon: ReactNode;
  text: ReactNode;
  /** Verdict colour; the weight and tracking come from the shell. */
  className: string;
}

interface Props {
  badge: { label: ReactNode; className: string };
  headline?: Headline;
  body?: ReactNode;
  timeZone?: string;
  /**
   * Collapses the card to its byline and badge - no rail, bulb, headline,
   * body or footer. Used where the recommendation's detail is withheld but
   * the verdict is still shown.
   */
  compact?: boolean;
}

/**
 * The Hakimo AI card's chrome: the gradient rail, the pulsing bulb, the
 * byline and badge row, and the timezone footer.
 *
 * Shared so the AI recommendation and the enterprise alarm details - which
 * sit in the same column and must look identical - cannot drift apart when
 * one of them is restyled.
 */
export function RecommendationCard({
  badge,
  headline,
  body,
  timeZone,
  compact = false,
}: Props) {
  return (
    <div className={`relative ${compact ? 'pt-4' : 'py-4'}`}>
      {!compact && (
        <div className="absolute bottom-0 left-[12.5px] top-0 w-0.5 bg-gradient-to-b from-yellow-400 via-amber-400 to-transparent dark:from-yellow-500 dark:via-amber-500" />
      )}

      <div className="relative flex items-start gap-2 pl-0">
        {!compact && (
          <div className="z-5 relative flex h-7 w-7 shrink-0 items-center justify-center">
            <div className="absolute inset-0 animate-pulse rounded-full bg-yellow-400/20 dark:bg-yellow-400/10" />
            <div className="relative flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg shadow-yellow-500/30">
              <LightBulbIcon className="h-4 w-4 text-white" />
            </div>
          </div>
        )}

        <div className="flex-1 space-y-3 pt-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SparklesIcon className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
              <span className="text-sm font-bold tracking-wide text-gray-900 dark:text-gray-100">
                Hakimo AI
              </span>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider ${badge.className}`}
            >
              {badge.label}
            </span>
          </div>

          {!compact && headline && (
            <div className="flex items-center gap-2.5">
              <div className="shrink-0">{headline.icon}</div>
              <h4
                className={`font-bold leading-tight tracking-wide ${headline.className}`}
              >
                {headline.text}
              </h4>
            </div>
          )}

          {!compact && body}

          {!compact && timeZone && (
            <div className="flex items-center gap-1.5 opacity-60">
              <GlobeAltIcon className="h-3.5 w-3.5" />
              <span className="text-[11px] font-medium uppercase tracking-wide">
                {timeZone}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RecommendationCard;
